export type FolderType = "all" | "integration" | "migration" | "sftp";

export interface GetFileSystemRequest {
  prefix: string;
  continuation_token: string | null;
  search: string | null;
  limit: number | null;
  folder_type?: FolderType;
  page?: number;
  sort_field?: "name" | "date" | "size";
  sort_order?: "asc" | "desc";
}

export interface DownloadFileRequest {
  source: string;
  fileName: string;
}

export interface DownloadArchiveRequest {
  source: string;
  fileName: string;
}

export type FolderSyncStatus = "syncing" | "ready" | "failed";

export interface FileSystemResponse {
  continuation_token: string | null;
  folders: FileItem[];
  show_filter: boolean | null;
  show_sftp_filter?: boolean;
  total_count?: number;
  total_pages?: number;
  current_page?: number;
  has_direct_files?: boolean;
  sync_status?: FolderSyncStatus;
  client_folder_prefix?: string | null;
}

export interface FileItem {
  fname: string;
  ftype: string;
  fsource: string;
  last_modified?: string;
  file_size?: number;
  file_count?: number;
  subfolder_count?: number;
  direct_file_count?: number;
  has_files_in_subtree?: boolean;
  sync_status?: FolderSyncStatus;
  is_sftp_folder?: boolean;
  is_uploading?: boolean;
  is_creating?: boolean;
  user_folder_id?: number;
  is_integration?: boolean;
  editable_folder_name?: string;
  root_list_owner_prefix?: string;
}

export interface DisconnectUserFolderRequest {
  folder_id: number;
}

export type FolderDisconnectTaskStatus =
  | "queued"
  | "processing"
  | "completed"
  | "failed";

export interface DisconnectUserFolderResponse {
  success: boolean;
  queued: true;
  job_id: string;
  status: FolderDisconnectTaskStatus;
  mode?: string;
}

export interface FolderDisconnectStatusRequest {
  job_id: string;
}

export interface FolderDisconnectStatusResponse {
  success: boolean;
  job_id: string;
  status: FolderDisconnectTaskStatus;
  folder_display_name?: string;
  error_message?: string | null;
}

export interface UpdateUserFolderDisplayNameRequest {
  folder_id: number;
  folder: string;
}

export interface UpdateUserFolderDisplayNameResponse {
  success: boolean;
  message?: string;
  folder_id: number;
  folder: string;
  folder_prefix: string;
  is_integration: boolean;
  is_sftp_folder: boolean;
  connections_match_id: number | null;
  sync_status: FolderSyncStatus;
}

export interface BrowserFolderCreateRequest {
  prefix: string;
  folder_name: string;
}

export interface BrowserFolderRenameRequest {
  prefix: string;
  parent_prefix: string;
  new_name: string;
}

export interface BrowserUploadPresignRequest {
  prefix: string;
  file_name: string;
  content_type?: string;
}

export interface BrowserUploadFinalizeRequest {
  prefix: string;
  file_name: string;
  file_size: number;
  content_type?: string;
}

export interface BrowserDeleteFileRequest {
  prefix: string;
  source: string;
}

export interface BrowserRenameFileRequest {
  prefix: string;
  source: string;
  new_name: string;
}

export interface BrowserDeleteFolderRequest {
  prefix: string;
  parent_prefix: string;
}

export interface BrowserActionResponse {
  success: boolean;
  message?: string;
}

export interface BrowserFolderCreateResponse extends BrowserActionResponse {
  item: FileItem;
}

export interface BrowserFolderRenameResponse extends BrowserActionResponse {
  item: Pick<FileItem, "fname" | "ftype" | "fsource">;
}

export interface BrowserUploadPresignResponse extends BrowserActionResponse {
  upload_url: string;
  key: string;
  headers: Record<string, string>;
}

export interface BrowserUploadFinalizeResponse extends BrowserActionResponse {
  created: boolean;
  item: FileItem;
}

export interface BrowserDeleteFileResponse extends BrowserActionResponse {
  deleted: boolean;
  db_deleted?: number;
  source: string;
}

export interface BrowserRenameFileResponse extends BrowserActionResponse {
  item: Pick<
    FileItem,
    "fname" | "ftype" | "fsource" | "file_size" | "last_modified"
  >;
}

export interface BrowserMoveFileRequest {
  source: string;
  destination_prefix: string;
  prefix: string;
}

export interface BrowserMoveFileResponse extends BrowserActionResponse {
  item: Pick<
    FileItem,
    "fname" | "ftype" | "fsource" | "file_size" | "last_modified"
  >;
}

export interface BrowserDeleteFolderResponse extends BrowserActionResponse {
  queued: boolean;
  folder_path: string;
  mode?: string;
}

import type { AdminConnectionsMatch } from "./adminUsers";

export interface ConnectFolderIntegrationMatch extends AdminConnectionsMatch {
  user_id: number;
  client_name: string;
}

export interface ConnectFolderIntegrationsResponse {
  matches: ConnectFolderIntegrationMatch[];
}

export interface ConnectFolderOptionItem {
  name: string;
  prefix: string;
}

export interface ConnectFolderOptionsResponse extends BrowserActionResponse {
  bucket: string;
  base_prefix: string;
  items: ConnectFolderOptionItem[];
}

export interface ConnectFolderOptionsRequest {
  user_id?: number;
  is_sftp_bucket: boolean;
  prefix?: string;
}

export interface ConnectFolderStorageCreateRequest {
  is_sftp_bucket: boolean;
  prefix: string;
  folder_name: string;
}

export interface ConnectFolderStorageCreateResponse extends BrowserActionResponse {
  bucket: string;
  prefix: string;
  name: string;
}

export interface ConnectFolderStorageDeleteRequest {
  is_sftp_bucket: boolean;
  user_id?: number;
  prefix: string;
}

export interface ConnectFolderStorageDeleteResponse extends BrowserActionResponse {
  bucket?: string;
  prefix?: string;
}

export interface ConnectFolderCreateRequest {
  user_id: number;
  folder: string;
  folder_prefix: string;
  is_sftp_bucket: boolean;
  is_integration?: boolean;
  connections_match_id?: number | null;
}

export interface ConnectFolderCreateResponse {
  success: boolean;
  message?: string;
  folder_id: number;
  user_id: number;
  folder: string;
  folder_prefix: string;
  is_sftp_bucket: boolean;
  is_integration: boolean;
  sync_status: FolderSyncStatus;
}

export interface FolderSyncStatusResponse {
  success: boolean;
  folder_id: number;
  sync_status: FolderSyncStatus;
}

export interface DownloadFileResponse {
  next_token: string | null;
  file_urls: string[];
}

export interface DownloadArchiveErrorResponse {
  error: string;
}

export interface DownloadArchiveUrlResponse {
  download_url: string;
  filename: string;
}

export interface DownloadArchivePresignedResponse {
  file_entries: Array<{ url: string; relative_path: string }>;
  next_token?: string | null;
  filename: string;
}

export interface TopFolderZipAsyncStartResponse {
  job_id: string;
  status: string;
  reused?: boolean;
}

export type StartTopFolderZipResponse = TopFolderZipAsyncStartResponse;

export type StartTopFolderZipBatchOkRow = {
  source: string;
  ok: true;
  job_id: string;
  status: string;
  reused?: boolean;
};

export type StartTopFolderZipBatchErrRow = {
  source: string;
  ok: false;
  error: string;
};

export type StartTopFolderZipBatchResultRow =
  | StartTopFolderZipBatchOkRow
  | StartTopFolderZipBatchErrRow;

export interface StartTopFolderZipBatchResponse {
  results: StartTopFolderZipBatchResultRow[];
}

export interface TopFolderZipReadyBadgesResponse {
  ready: Record<string, boolean>;
}

export type DownloadArchiveResponse =
  | Blob
  | DownloadArchiveErrorResponse
  | DownloadArchiveUrlResponse
  | DownloadArchivePresignedResponse;

export const isDownloadArchiveError = (
  response: DownloadArchiveResponse,
): response is DownloadArchiveErrorResponse => {
  return (
    typeof response === "object" && response !== null && "error" in response
  );
};

export const isDownloadArchiveBlob = (
  response: DownloadArchiveResponse,
): response is Blob => {
  return response instanceof Blob;
};

export const isDownloadArchiveUrl = (
  response: DownloadArchiveResponse,
): response is DownloadArchiveUrlResponse => {
  return (
    typeof response === "object" &&
    response !== null &&
    "download_url" in response &&
    !("file_entries" in response)
  );
};

export const isDownloadArchivePresigned = (
  response: DownloadArchiveResponse,
): response is DownloadArchivePresignedResponse => {
  return (
    typeof response === "object" &&
    response !== null &&
    "file_entries" in response &&
    Array.isArray((response as DownloadArchivePresignedResponse).file_entries)
  );
};
